import { useParams, Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const blogContent: Record<string, {
  title: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
  content: string[];
}> = {
  'missed-calls-revenue-killer-2025': {
    title: 'Missed Calls: The Silent Revenue Killer in 2025',
    category: '2025 Trends',
    date: 'January 18, 2025',
    readTime: '8 min read',
    image: 'https://public.readdy.ai/ai/img_res/471c842cd238a05fb17ea40ae0426369.jpg',
    content: [
      'In 2025, the phone remains the most valuable lead generation channel for service businesses. Yet our latest research reveals a startling truth: 62% of business calls go unanswered during regular business hours.',
      'For a business receiving just 100 calls per day, this translates to 62 missed opportunities. At an average lead value of $1,200 for service industries like law, real estate, and home services, that\'s $74,400 in potential revenue slipping through the cracks every single month.',
      '<strong>The 5-Minute Window</strong>',
      'Our data shows that 85% of callers who don\'t reach a live person won\'t call back. Instead, they move to a competitor within 5 minutes. In the age of instant gratification, patience for voicemail has evaporated.',
      '<strong>Why Calls Go Unanswered</strong>',
      'The reasons are predictable but persistent: staff are busy with existing clients, lunch breaks create coverage gaps, and after-hours calls (which represent 73% of high-value leads) go straight to voicemail.',
      '<strong>The Real Cost Breakdown</strong>',
      'Let\'s do the math for a typical service business:',
      '• 100 calls/day × 62% missed = 62 missed calls',
      '• 62 missed calls × $1,200 average value = $74,400/month',
      '• $74,400 × 12 months = $892,800/year in lost revenue',
      '<strong>The Solution: AI Phone Agents</strong>',
      'AI phone agents answer every call in under 2 rings, 24/7. They qualify leads, book appointments, and hand off to your team only when necessary. Businesses using AI phone agents report capturing 68% more leads and reducing missed calls to near zero.',
      'The technology has matured significantly in 2025. Modern AI agents sound natural, handle complex conversations, and integrate seamlessly with your existing CRM and calendar systems.',
      '<strong>Taking Action</strong>',
      'If you\'re losing revenue to missed calls, the solution is clear. Calculate your own missed call cost using the formula above, then consider whether an AI phone agent could close that gap.',
    ],
  },
  'after-hours-calls-2025-data': {
    title: 'The 2025 After-Hours Call Report: What the Data Shows',
    category: 'Research',
    date: 'January 15, 2025',
    readTime: '10 min read',
    image: 'https://readdy.ai/api/search-image?query=Night%20time%20city%20office%20building%20with%20lights%20on%20showing%20late%20night%20business%20operations%20and%2024%20hour%20service%20concept%20with%20modern%20architecture&width=1200&height=600&seq=blog7detail&orientation=landscape',
    content: [
      'New research from our analysis of over 500,000 business calls reveals a critical insight: 73% of high-value leads call outside traditional business hours.',
      'This finding challenges the conventional wisdom that business happens 9-to-5. In reality, your best prospects are calling when you\'re least likely to answer.',
      '<strong>When High-Value Leads Actually Call</strong>',
      'Our data breaks down calling patterns by time:',
      '• 6 PM - 9 PM: 34% of high-value calls (people calling after their own workday)',
      '• 9 PM - 6 AM: 18% of high-value calls (urgent needs, different time zones)',
      '• Weekends: 21% of high-value calls (people with time to research and decide)',
      '• Business hours: Only 27% of high-value calls',
      '<strong>Why After-Hours Callers Convert Better</strong>',
      'Callers who reach out after hours demonstrate higher intent. They\'ve taken time outside their busy day to make the call, indicating genuine interest rather than casual browsing.',
      'Our data shows after-hours callers convert at 2.3x the rate of business-hours callers when they reach a live response.',
      '<strong>The Voicemail Problem</strong>',
      'Here\'s where it gets painful: 91% of after-hours calls go to voicemail. Of those, only 15% leave a message. The rest? They call your competitor.',
      '<strong>Industry-Specific Patterns</strong>',
      'Different industries see different after-hours patterns:',
      '• Law firms: 67% of personal injury calls come after 6 PM',
      '• Real estate: 78% of buyer inquiries happen on weekends',
      '• Roofing: Storm-related calls spike 400% after hours during weather events',
      '• Recruiting: 82% of passive candidates call during lunch or after work',
      '<strong>The 24/7 Imperative</strong>',
      'The data is clear: if you\'re not answering calls 24/7, you\'re missing the majority of your best leads. AI phone agents provide the solution, ensuring every after-hours call gets a professional, immediate response.',
    ],
  },
  'ai-vs-voicemail-2025': {
    title: 'AI Phone Agents vs. Voicemail: 2025 Conversion Rates',
    category: 'Comparison',
    date: 'January 12, 2025',
    readTime: '7 min read',
    image: 'https://readdy.ai/api/search-image?query=Futuristic%20AI%20assistant%20concept%20with%20voice%20waves%20and%20neural%20network%20visualization%20representing%20intelligent%20phone%20conversation%20technology%20in%20teal%20and%20white%20colors&width=1200&height=600&seq=blog2detail&orientation=landscape',
    content: [
      'The voicemail system was revolutionary in 1980. In 2025, it\'s a lead killer. Our comparative analysis of 100,000+ calls reveals the stark difference between voicemail and AI phone agents.',
      '<strong>The Voicemail Reality Check</strong>',
      'When a caller reaches voicemail:',
      '• 85% hang up without leaving a message',
      '• 12% leave a message but never get called back in time',
      '• Only 3% successfully connect with the business',
      'That\'s a 97% failure rate for your most valuable lead channel.',
      '<strong>AI Phone Agent Performance</strong>',
      'When a caller reaches an AI phone agent:',
      '• 94% engage in conversation',
      '• 78% complete the qualification process',
      '• 62% book an appointment or get transferred to the right person',
      'That\'s a 4x improvement in lead capture compared to voicemail.',
      '<strong>Why Callers Hate Voicemail</strong>',
      'Our survey of 2,000 consumers revealed the top reasons people won\'t leave voicemails:',
      '1. "I don\'t know when or if I\'ll get a callback" (67%)',
      '2. "I\'ll just call someone else" (54%)',
      '3. "Voicemail feels impersonal and outdated" (48%)',
      '4. "I forget what I wanted to say" (31%)',
      '<strong>The AI Advantage</strong>',
      'Modern AI phone agents address every voicemail objection:',
      '• Immediate response - no waiting for callbacks',
      '• Natural conversation - feels like talking to a person',
      '• Instant booking - appointments scheduled on the spot',
      '• Smart handoffs - complex issues routed to humans immediately',
      '<strong>Cost Comparison</strong>',
      'Voicemail costs you leads. AI phone agents cost a fraction of a missed opportunity:',
      '• Average missed lead value: $1,200',
      '• AI phone agent cost per call: ~$2-5',
      '• ROI: 240x-600x return on investment',
      '<strong>The Verdict</strong>',
      'Voicemail had its time. In 2025, businesses that rely on voicemail are leaving money on the table. AI phone agents represent the new standard for professional call handling.',
    ],
  },
};

const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? blogContent[slug] : null;

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Header />
        <main className="flex-1 pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-navy mb-4">Article not found</h1>
            <Link to="/blog" className="text-teal hover:underline">Back to Blog</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Hero */}
        <section className="bg-gradient-to-br from-navy via-navy/95 to-navy/90 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/blog" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
              <i className="ri-arrow-left-line"></i>
              Back to Blog
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-semibold text-teal uppercase tracking-wide bg-teal/20 px-3 py-1 rounded-full">{post.category}</span>
              <span className="text-xs text-white/60">{post.readTime}</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">{post.title}</h1>
            <p className="text-white/80">{post.date}</p>
          </div>
        </section>

        {/* Featured Image */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <img src={post.image} alt={post.title} className="w-full h-64 md:h-96 object-cover" />
          </div>
        </div>

        {/* Content */}
        <article className="py-12">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="prose prose-lg max-w-none">
              {post.content.map((paragraph, index) => (
                <p 
                  key={index} 
                  className="text-gray-700 mb-6 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: paragraph }}
                />
              ))}
            </div>

            {/* Share */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-4">Share this article</p>
              <div className="flex gap-3">
                <a href="#" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-teal hover:text-white transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-teal hover:text-white transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-teal hover:text-white transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
              </div>
            </div>
          </div>
        </article>

        {/* CTA */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-navy mb-4">Stop losing leads to missed calls</h2>
            <p className="text-gray-600 mb-8">See how PointCalls AI phone agents can capture every opportunity for your business.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="https://tidycal.com/mattv/15-minute-meeting"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-teal text-white text-lg font-semibold rounded-lg hover:bg-teal/90 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Book a Demo
              </a>
              <a 
                href="tel:+16787376473" 
                className="px-8 py-4 border-2 border-navy text-navy font-semibold rounded-lg hover:bg-navy hover:text-white transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Call Live Demo
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
